import React, { useEffect, useState, useCallback } from "react";
import Moment from "react-moment";
import { getCurrentUser, getLoggedUserDetails, loadUserDetails } from "../app/store/auth";
import { useDispatch, useSelector } from "react-redux";
import { getComplianceReport } from "../app/store/report";
import { useLoadComplianceReportQuery } from "../app/store/reportApiSlice";
import useDocDownload from "../app/hooks/useDocDownload";
import { getComponentUserStatus, getToolsCount, UpdateComponentsUserStatus } from "../app/store/tools";
import { setCurrentPageTitle, setCurrentRoute } from "../app/store/layoutUI";

const COMPLIANCE_STYLES = {
  shell: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%"
  },
  hero: {
    borderRadius: "0px",
    padding: "14px 20px",
    border: "1px solid rgba(219, 234, 254, 0.95)",
    background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 52%, #f8fafc 100%)",
    boxShadow: "0 10px 30px rgba(37, 99, 235, 0.05)"
  },
  heroRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    flexWrap: "wrap"
  },
  heroCopy: {
    minWidth: "260px",
    flex: "1 1 480px"
  },
  heroTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  heroIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "18px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    flexShrink: 0
  },
  heroTitle: {
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: 1.2,
    color: "#0f172a",
    margin: "0 0 3px 0",
    letterSpacing: "-0.01em"
  },
  heroText: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#475569",
    margin: 0,
    maxWidth: "760px"
  },
  heroAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "transparent",
    border: "none",
    color: "#2563eb",
    padding: "0",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "none"
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    marginTop: "20px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: "0px",
    padding: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "4px"
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1,
    color: "#0f172a",
    marginBottom: "4px"
  },
  panel: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: "0px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
    overflow: "hidden"
  },
  cardHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  titleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#ffffff",
    color: "#000000",
    fontSize: "14px",
    border: "1px solid #e5e5e5",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    flexShrink: 0
  },
  panelTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#000000",
    margin: 0
  },
  panelText: {
    fontSize: "13px",
    color: "#666666",
    margin: 0
  },
  cardBody: {
    padding: "0"
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff"
  },
  table: {
    width: "100%",
    marginBottom: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    borderBottom: "1px solid #e5e5e5",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    padding: "14px 16px",
    whiteSpace: "nowrap",
    verticalAlign: "top"
  },
  cell: {
    padding: "14px 16px",
    borderBottom: "1px solid rgba(241, 245, 249, 1)",
    verticalAlign: "top",
    fontWeight: 600
  },
  dateText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "#0f172a"
  },
  yearBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "0px",
    backgroundColor: "#eff6ff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 600
  },
  notesText: {
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "#475569",
    whiteSpace: "pre-wrap",
    fontWeight: 600
  },
  docsList: {
    display: "grid",
    gap: "8px"
  },
  docButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "transparent",
    border: "none",
    color: "#2563eb",
    padding: "0",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "none",
    textAlign: "left"
  },
  emptyState: {
    padding: "42px 24px",
    textAlign: "center",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  emptyIcon: {
    width: "64px",
    height: "64px",
    margin: "0 auto 14px auto",
    borderRadius: "0px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "26px"
  },
  emptyTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center"
  },
  emptyText: {
    margin: "10px auto 0 auto",
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.6,
    fontWeight: 600,
    textAlign: "center"
  }
};

const COMPLIANCE_RPT_ROUTE = [
  { name: "Reports", url: "" },
  { name: "Compliance Documents", url: "" }
];

const ComplianceRpt = () => {

  const [complianceData, setComplianceData] = useState([]);
  const [partnerCompliance, setPartnerCompliance] = useState(false);

  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const complianceReportSelector = useSelector(getComplianceReport);
  const componentUserStatusSelector = useSelector(getComponentUserStatus);
  const toolsCountSelector = useSelector(getToolsCount);

  const [handleDownloadFile] = useDocDownload();

  const usrType = localStorage.getItem("userType");

  const currentUser = getCurrentUser();
  const dispatch = useDispatch();

  const usrId = currentUser?.linkUser || currentUser?.id;
  const isCandidate = usrType === "candidate";

  const complianceReportId = isCandidate ? usrId : userDetailsSelector?.partnerid;

  const { refetch: refetchComplianceReport, isLoading: isLoadingCompliance, isFetching: isFetchingCompliance } = useLoadComplianceReportQuery(
    complianceReportId,
    { skip: !complianceReportId }
  );

  const isInitialLoading = isLoadingCompliance || (!complianceReportId && !complianceReportSelector);
  const isDataFetching = isFetchingCompliance;
  const isDataLoading = isInitialLoading || (isDataFetching && complianceData.length === 0);

  useEffect(() => {
    dispatch(setCurrentPageTitle("Compliance Documents"));
    dispatch(setCurrentRoute(COMPLIANCE_RPT_ROUTE));

    let targetUsrId = currentUser?.linkUser || currentUser?.id;
    if (usrType !== "candidate") {
      if (!userDetailsSelector && targetUsrId)
        dispatch(loadUserDetails(targetUsrId));
      setPartnerCompliance(true);
    }
  }, [dispatch, usrType, currentUser, userDetailsSelector]);

  const handleUpdateComponent = useCallback((compItems, ciid) => {
    const comp = componentUserStatusSelector.filter(x => x.compId === 25);
    let updCompItems = "";
    if (comp.length > 0) {
      updCompItems = comp[0].compItems;
    }
    const compObj = {
      compName: "Compliance",
      performerId: currentUser.id,
      compItems: compItems,
      ciid: ciid,
      wiid: 0,
      iiid: 0,
      compId: 25
    }
    if (compItems !== "" && compItems !== updCompItems) {
      dispatch(UpdateComponentsUserStatus(compObj));
    }
  }, [componentUserStatusSelector, currentUser?.id, dispatch]);

  useEffect(() => {
    if (complianceReportSelector.length > 0) {
      setComplianceData(complianceReportSelector);
      let compItems = "";
      complianceReportSelector.forEach((x, key) => {
        if (key > 0) compItems += ",";
        compItems += x.id;
      });

      const appObj = toolsCountSelector.filter(x => x.key === 0);
      let appCiid = 0;
      if (appObj.length > 0)
        appCiid = appObj[0].value;

      handleUpdateComponent(compItems, appCiid);
    } else {
      setComplianceData([]);
    }
  }, [complianceReportSelector, toolsCountSelector, handleUpdateComponent]);

  const handleRefresh = useCallback(() => {
    let targetUsrId = currentUser?.linkUser || currentUser?.id;
    if (usrType !== "candidate" && targetUsrId) {
      dispatch(loadUserDetails(targetUsrId));
    }
    if (refetchComplianceReport) {
      refetchComplianceReport();
    }
  }, [currentUser, dispatch, refetchComplianceReport, usrType]);

  const totalReports = complianceData.length;
  const totalDocuments = complianceData.reduce((count, item) => count + (item.complianceDocs?.length || 0), 0);
  const coveredYears = new Set(complianceData.map((item) => item.year).filter(Boolean)).size;

  return (
    <React.Fragment>
      <style>{`
        .compliance-stat-card:hover {
          transform: translateY(-2px);
          border-color: rgba(191, 219, 254, 0.9) !important;
          box-shadow: 0 10px 25px rgba(37, 99, 235, 0.07) !important;
        }
        .cr-link-btn,
        .cr-link-btn:hover,
        .cr-link-btn:focus,
        .cr-link-btn.focus,
        .cr-link-btn:active,
        .cr-link-btn:not(:disabled):not(.disabled):active {
          background: none !important;
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
          padding: 0 !important;
        }
        .cr-link-btn:hover {
          text-decoration: underline !important;
          color: #1d4ed8 !important;
        }
      `}</style>

      <div style={COMPLIANCE_STYLES.shell}>
        {/* Table Panel */}
        <div style={COMPLIANCE_STYLES.panel}>
          <div style={COMPLIANCE_STYLES.cardHeader}>
            <div style={COMPLIANCE_STYLES.headerRow}>
              <div style={COMPLIANCE_STYLES.titleRow}>
                <span style={COMPLIANCE_STYLES.titleIcon}>
                  <i className="fa fa-list-alt" aria-hidden="true"></i>
                </span>
                <div>
                  <h2 style={COMPLIANCE_STYLES.panelTitle}>Compliance Records</h2>
                  <p style={COMPLIANCE_STYLES.panelText}>Browse compliance reports, notes, and attached documents.</p>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm cr-link-btn"
                style={COMPLIANCE_STYLES.heroAction}
                onClick={handleRefresh}
                disabled={isDataFetching}
                title="Refresh Compliance Documents"
              >
                <i className={`fa fa-refresh ${isDataFetching ? "fa-spin" : ""}`} aria-hidden="true"></i>{" "}
                {isDataFetching ? "Fetching..." : "Refresh"}
              </button>
            </div>
          </div>

          {isDataFetching && complianceData.length > 0 && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 18px",
              background: "#eff6ff",
              borderTop: "1px solid #bfdbfe",
              borderBottom: "1px solid #bfdbfe",
              color: "#1e40af",
              fontSize: "12px",
              fontWeight: 600
            }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                <i className="fa fa-circle-o-notch fa-spin" style={{ color: "#2563eb", fontSize: "13px" }}></i>
                <span>Fetching updated compliance documents...</span>
              </div>
              <span style={{ fontSize: "11px", color: "#3b82f6", fontWeight: 500 }}>Updating</span>
            </div>
          )}

          <div style={COMPLIANCE_STYLES.cardBody}>
            <div style={{ padding: "20px" }}>
              <div style={{ ...COMPLIANCE_STYLES.statGrid, marginTop: 0 }}>
                <div className="compliance-stat-card" style={COMPLIANCE_STYLES.statCard}>
                  <div style={COMPLIANCE_STYLES.statIcon}>
                    <i className="fa fa-file-text-o" aria-hidden="true"></i>
                  </div>
                  <div style={COMPLIANCE_STYLES.statContent}>
                    <div style={COMPLIANCE_STYLES.statLabel}>Reports</div>
                    <div style={COMPLIANCE_STYLES.statValue}>
                      {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : totalReports}
                    </div>
                  </div>
                </div>
                <div className="compliance-stat-card" style={COMPLIANCE_STYLES.statCard}>
                  <div style={{ ...COMPLIANCE_STYLES.statIcon, background: "#ecfdf5", color: "#047857" }}>
                    <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                  </div>
                  <div style={COMPLIANCE_STYLES.statContent}>
                    <div style={COMPLIANCE_STYLES.statLabel}>Documents</div>
                    <div style={COMPLIANCE_STYLES.statValue}>
                      {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : totalDocuments}
                    </div>
                  </div>
                </div>
                <div className="compliance-stat-card" style={COMPLIANCE_STYLES.statCard}>
                  <div style={{ ...COMPLIANCE_STYLES.statIcon, background: "#fffbeb", color: "#b45309" }}>
                    <i className="fa fa-calendar" aria-hidden="true"></i>
                  </div>
                  <div style={COMPLIANCE_STYLES.statContent}>
                    <div style={COMPLIANCE_STYLES.statLabel}>Years Covered</div>
                    <div style={COMPLIANCE_STYLES.statValue}>
                      {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : coveredYears}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={COMPLIANCE_STYLES.tableWrap}>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0 compliance-rpt-table" style={COMPLIANCE_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>ID</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Date & Time Added</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Year</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Notes</th>
                      <th style={COMPLIANCE_STYLES.tableHeadCell}>Document Name</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: isDataFetching && complianceData.length > 0 ? 0.7 : 1, transition: "opacity 0.2s ease" }}>
                    {isDataLoading ? (
                      <tr>
                        <td colSpan="5" style={{ padding: "48px 20px", textAlign: "center", backgroundColor: "#ffffff" }}>
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "40px", height: "40px", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", color: "#000000", fontSize: "16px" }}>
                              <i className="fa fa-circle-o-notch fa-spin"></i>
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                              {isDataFetching ? "Fetching compliance data..." : "Loading compliance data..."}
                            </div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#64748b" }}>
                              Please wait while we retrieve compliance records.
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : complianceData.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ ...COMPLIANCE_STYLES.cell, padding: 0 }}>
                          <div style={COMPLIANCE_STYLES.emptyState}>
                            <div style={COMPLIANCE_STYLES.emptyIcon}>
                              <i className="fa fa-check-square-o" aria-hidden="true"></i>
                            </div>
                            <h4 style={COMPLIANCE_STYLES.emptyTitle}>No compliance documents found</h4>
                            <p style={COMPLIANCE_STYLES.emptyText}>
                              Compliance records will appear here once reports and supporting files are available for your account or partner.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      complianceData.map((item, key) => (
                        <tr key={item.id || key}>
                          <td style={COMPLIANCE_STYLES.cell}>
                            <span style={{ color: "#2563eb", fontWeight: 700 }}>#</span>{" "}
                            <span style={{ fontWeight: 600, color: "#0f172a" }}>{item.id || key + 1}</span>
                          </td>
                          <td style={COMPLIANCE_STYLES.cell}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                                <Moment format="MMM Do, YYYY">
                                  {item.startDateTime}
                                </Moment>
                              </span>
                              <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                                <Moment format="h:mm a">
                                  {item.startDateTime}
                                </Moment>
                              </span>
                            </div>
                          </td>
                          <td style={COMPLIANCE_STYLES.cell}>
                            <span style={COMPLIANCE_STYLES.yearBadge}>{item.year || "N/A"}</span>
                          </td>
                          <td style={COMPLIANCE_STYLES.cell}>
                            <div style={COMPLIANCE_STYLES.notesText}>{item.notes || "No notes added."}</div>
                          </td>
                          <td style={COMPLIANCE_STYLES.cell}>
                            <div style={COMPLIANCE_STYLES.docsList}>
                              {(item.complianceDocs || []).map((doc, docIndex) => (
                                <button
                                  key={doc.id || `${item.id || key}-${docIndex}`}
                                  className="btn btn-sm cr-link-btn"
                                  style={COMPLIANCE_STYLES.docButton}
                                  onClick={() => handleDownloadFile(doc)}
                                >
                                  <i className="fa fa-file-text-o" aria-hidden="true" style={{ color: "#2563eb" }}></i>
                                  {doc.documentName}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ComplianceRpt;
